import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu, Layers, Wifi, Terminal, Package,
  Users, CheckCircle, Smile, FileText, ArrowRight, ChevronRight
} from 'lucide-react';
import apiClient from '../config/axios';
import './Home.css';

const iconMap = {
  'microchip': Cpu,
  'circuit-board': Layers,
  'cpu': Cpu,
  'wifi': Wifi,
  'terminal': Terminal,
  'box': Package,
  'users': Users,
  'check-circle': CheckCircle,
  'smile': Smile,
  'file-text': FileText
};

const Home = () => {
  const [services, setServices] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [projects, setProjects] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, statsRes, projectsRes] = await Promise.all([
        apiClient.get('/api/services'),
        apiClient.get('/api/statistics'),
        apiClient.get('/api/projects')
      ]);
      setServices(servicesRes.data.data?.slice(0, 6) || getDefaultServices());
      setStatistics(statsRes.data.data || getDefaultStats());
      setProjects(projectsRes.data.data?.slice(0, 3) || getDefaultProjects());
    } catch (error) {
      console.log('Using default data');
      setServices(getDefaultServices());
      setStatistics(getDefaultStats());
      setProjects(getDefaultProjects());
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDefaultServices = () => [
    { id: 1, title: 'Firmware Development', description: 'Expert firmware solutions that reduce hardware costs and improve battery efficiency.', icon: 'microchip' },
    { id: 2, title: 'Hardware Design', description: 'Optimal schematic designs leveraging extensive product experience.', icon: 'circuit-board' },
    { id: 3, title: 'PCB Design', description: 'High-quality PCB layouts using multiple design tools.', icon: 'cpu' },
    { id: 4, title: 'IoT Solutions', description: 'Comprehensive IoT solutions for seamless device connectivity.', icon: 'wifi' },
    { id: 5, title: 'Embedded Linux', description: 'Custom embedded Linux development for industrial applications.', icon: 'terminal' },
    { id: 6, title: 'Product Development', description: 'End-to-end product development from concept to production.', icon: 'box' }
  ];

  const getDefaultStats = () => [
    { id: 1, label: 'Annual Partners', value: 545, icon: 'users' },
    { id: 2, label: 'Completed Projects', value: 56, icon: 'check-circle' },
    { id: 3, label: 'Happy Customers', value: 500, icon: 'smile' },
    { id: 4, label: 'Research Works', value: 320, icon: 'file-text' }
  ];

  const getDefaultProjects = () => [
    { id: 1, title: 'Weighbridge Automation', description: 'Advanced controller for unmanned weigh bridge operations.', category: 'Industrial Automation' },
    { id: 2, title: 'IoT Data Bridge', description: 'Enables legacy equipment to connect to modern IoT infrastructure.', category: 'IoT Solutions' },
    { id: 3, title: 'Industrial LED Controllers', description: 'Custom LED solutions for manufacturing applications.', category: 'LED Systems' }
  ];

  const renderIcon = (iconName, size = 32) => {
    const IconComponent = iconMap[iconName] || Cpu;
    return <IconComponent size={size} />;
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="container">
          <div className="hero-content">
            <h1>Innovative Embedded Solutions for Tomorrow</h1>
            <p>
              We provide cost-effective, smart embedded solutions that drive technological advancement.
              From firmware development to IoT solutions, we transform your ideas into reality.
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                Our Services
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics">
        <div className="container">
          <div className="stats-grid">
            {statistics.map((stat) => (
              <div key={stat.id} className="stat-item">
                <div className="stat-icon">{renderIcon(stat.icon, 28)}</div>
                <div className="stat-value">{stat.value}+</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>About Signature Embedded Systems</h2>
              <p className="tagline">INNOVATION IN EVERY TRACE</p>
              <p>
                Signature Embedded Systems is a leading provider of embedded solutions,
                specializing in firmware development, hardware design, and IoT solutions.
                Based in Hyderabad, we serve clients worldwide with innovative technology solutions.
              </p>
              <p>
                Our vision is to provide cost-effective, innovative smart solutions that
                drive technological advancement and empower businesses to achieve their
                digital transformation goals.
              </p>
              <Link to="/about" className="btn btn-outline">
                Learn More About Us <ChevronRight size={18} />
              </Link>
            </div>
            <div className="about-image">
              <div className="about-image-placeholder">
                <Layers size={80} />
                <span>Engineering Excellence</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section bg-light">
        <div className="container">
          <div className="section-title">
            <h2>Our Services</h2>
            <p>Comprehensive embedded solutions tailored to your needs</p>
          </div>
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card card">
                <div className="service-icon">{renderIcon(service.icon)}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to="/services" className="service-link">
                  Learn More <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="projects-section section">
        <div className="container">
          <div className="section-title">
            <h2>Featured Projects</h2>
            <p>Innovative solutions we've delivered for our clients</p>
          </div>
          <div className="projects-grid">
            {projects.map((project) => (
              <div key={project.id} className="project-card card">
                <div className="project-category">{project.category}</div>
                <h3>{project.title}</h3>
                <p>{project.description}</p>
              </div>
            ))}
          </div>
          <div className="projects-cta">
            <Link to="/projects" className="btn btn-primary">
              View All Projects <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Project?</h2>
            <p>
              Let's discuss how we can help you achieve your embedded systems goals.
              Contact us today for a free consultation.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Contact Us Today <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
